import { Plural, t, Trans } from '@lingui/macro';
import classnames from 'classnames';
import React, { FormEvent, FC, useState, useEffect, ReactNode, memo, ReactFragment } from 'react';
import nl2ReactBR from 'react-nl2br';
import { Link } from 'react-router-dom';

import Image from 'common/Image/Image';
import ImageSlider from 'common/ImageSlider/ImageSlider';
import RatingStars from 'common/RatingStars/RatingStars';
import { truncateWithEllipsis, removeExtraLineBreaks } from 'common/helpers/helpers';
import { SyncIcon } from 'common/icons/SyncIcon';
import Avatar from 'uikit/Avatar/Avatar';
import { LinkButton, Button } from 'uikit/Button';
import { TextP1, TextP2, TextP3, TextMeta } from 'uikit/Text';
import { Textarea } from 'uikit/Textarea';
import routes from 'utils/routeTranslator';

import {
  FeedbackReply,
  ProfileFeedbackReplyStatus,
  formatDate,
  sortCategories,
  Feedback as FeedbackModel,
} from './Feedback.helpers';
import './Feedback.scss';

const TRUNCATION_LENGTH = 250;
const TRUNCATE_IF_LONGER = 300;

type Props = FeedbackModel & {
  sendFeedbackReply?: (id: string, reply: string) => Promise<any>;
  providerCategoryName?: string;
};

const Feedback: FC<Props> = (props) => {
  const [showAddReply, setShowAddReply] = useState<boolean>(false);
  const [isExpandable, setIsExpandable] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [replyText, setReplyText] = useState<string>('');

  useEffect(() => {
    setIsExpandable(props.comment.length > TRUNCATE_IF_LONGER);
  }, [props.comment]);

  const submitReply = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (props.sendFeedbackReply) {
      setLoading(true);

      props
        .sendFeedbackReply(props.id, replyText)
        .then(() => setShowAddReply(false))
        .catch(() => setShowAddReply(false))
        .finally(() => setLoading(false));
    }
  };

  const renderProvider = (): ReactNode => {
    const { slug, avatar, name, city } = props.provider;
    const providerInfoText: string = [t`Wykonawca`, city, props.providerCategoryName]
      .filter((item) => Boolean(item))
      .join(', ');

    const innerContent = (
      <>
        {avatar && (
          <Image
            src={avatar}
            size="s"
            webp
            defaultJPEG
            multiPixelRatios
            alt={name}
            className="feedback__providerAvatar"
            lazy
          />
        )}
        <div className="feedback__providerContacts">
          <TextP1 className="feedback__providerName">{name}</TextP1>
          <TextMeta big className="feedback__providerInfo">
            {providerInfoText}
          </TextMeta>
        </div>
      </>
    );

    return (
      <div className="feedback__provider">
        {Boolean(slug) && (
          <Link to={routes.get('front::user.profile', { username: slug })} className="feedback__providerLink">
            {innerContent}
          </Link>
        )}

        {!slug && <div className="feedback__providerLink">{innerContent}</div>}
      </div>
    );
  };

  const renderTooltip = (categoryNames: string[]): ReactNode => {
    return (
      <div className="feedback__tooltip feedback__tooltip_bottom">
        <div className="feedback__tooltipContentWrapper">
          {categoryNames.map((category) => (
            <p className="feedback__categoryCountName" key={category}>
              {category}
            </p>
          ))}
        </div>
      </div>
    );
  };

  const renderCategoryName = (l4CategoryNames: string[], preferredCategoryName?: string): ReactFragment => {
    const sortedCategories = sortCategories(l4CategoryNames, preferredCategoryName);
    const [primaryCategory, ...restCategories] = sortedCategories;
    const restCategoriesLength = restCategories.length;

    return (
      <>
        <span className="feedback__categoryName">{primaryCategory}</span>
        {restCategoriesLength > 0 && (
          <span className="feedback__restCategories">
            <TextP1>
              <span className="feedback__categoryCount">
                <Plural value={restCategoriesLength} one="+# usługa" few="+# usługi" other="+# usług"></Plural>
              </span>
            </TextP1>
            {renderTooltip(restCategories)}
          </span>
        )}
      </>
    );
  };

  const renderComment = (): ReactNode => {
    const showAsIs = !isExpandable || isExpanded;
    const content = showAsIs ? props.comment : truncateWithEllipsis(props.comment, TRUNCATION_LENGTH);

    const commentCN = classnames('feedback__commentWrap', {
      feedback__commentWrap_expandable: isExpandable,
      feedback__commentWrap_expanded: isExpanded,
    });

    return (
      <div className={commentCN}>
        <TextP2 className="feedback__comment">{nl2ReactBR(removeExtraLineBreaks(content))}</TextP2>
        <button type="button" className="feedback__expand" onClick={() => setIsExpanded(true)}>
          <Trans>Pokaż więcej</Trans>
        </button>
      </div>
    );
  };

  const renderAddReply = (): ReactNode => {
    const addReplyCN = classnames('feedback__addReply', {
      feedback__addReply_visible: showAddReply,
    });

    return (
      <form className={addReplyCN} onSubmit={submitReply}>
        <span className="feedback__addReplyTitle">
          <Trans>Zanim Twój komentarz zostanie opublikowany, sprawdzi go nasz zespół Obsługi Użytkownika.</Trans>
        </span>
        <Textarea
          className="feedback__addReplyText"
          placeholder={t`Napisz komentarz…`}
          value={replyText}
          onChange={(event) => setReplyText(event.currentTarget.value)}
        />

        <div className="feedback__addReplySubmitWrap">
          <Button className="feedback__addReplySubmit" type="submit" disabled={!replyText} isLoading={loading}>
            <Trans>Wyślij</Trans>
          </Button>
        </div>
      </form>
    );
  };

  const renderReply = (reply: FeedbackReply): ReactNode => {
    if (reply.status !== ProfileFeedbackReplyStatus.approved && !props.isSameUser) return null;

    return (
      <div className="feedback__reply">
        <h5 className="feedback__replyTitle">
          <Trans>Odpowiedź od wykonawcy</Trans>:
        </h5>
        <div className="feedback__replyAuthor feedback__author">
          {props.provider.avatar && <img className="feedback__authorAvatar" src={props.provider.avatar} alt="" />}
          <TextP3 className="feedback__authorInfo">
            <span className="feedback__authorName">{props.provider.name}</span>
            <span className="feedback__authorCity">
              <Trans>Wykonawca</Trans>
            </span>
          </TextP3>
        </div>
        <p className="feedback__replyText">{nl2ReactBR(reply.comment)}</p>
        {reply.status === ProfileFeedbackReplyStatus.pending && props.isSameUser && (
          <div className="feedback__replyInfo">
            <div className="feedback__replyInfoIcon">
              <SyncIcon />
            </div>
            <TextMeta big className="feedback__replyStatus">
              <Trans>Twój komentarz czeka na sprawdzenie</Trans>
            </TextMeta>
          </div>
        )}
      </div>
    );
  };

  const { avatar, name, city } = props.user;

  return (
    <div className="feedback">
      {props.provider && props.showProvider && renderProvider()}
      <div className="feedback__author">
        {avatar && <Avatar avatarUrl={avatar} className="feedback__authorAvatar" />}
        <TextP3 className="feedback__authorInfo">
          <span className="feedback__authorName">{name}</span>
          {city && <span className="feedback__authorCity">{city}</span>}
        </TextP3>
      </div>
      <div className="feedback__title">
        {props.l4CategoryNames && renderCategoryName(props.l4CategoryNames, props.preferredCategoryName)}
        {props.isSameUser && !props.seen && (
          <span className="feedback__new">
            <Trans>Nowa!</Trans>
          </span>
        )}
      </div>
      {props.value && <RatingStars className="feedback__rating" rating={props.value} size="small" />}
      {props.comment && renderComment()}
      {props.files && props.files.length > 0 && (
        <ImageSlider
          images={props.files.map((file) => ({ url: file }))}
          maxVisibleImages={5}
          staticPreview
          clickable
          className="feedback__imageSlider"
          imageClassName="feedback__image"
        />
      )}

      <div className="feedback__footer">
        <TextMeta big bold className="feedback__footerText">
          <time dateTime={props.createdAt}>{formatDate(props.createdAt)}</time>
          {!props.isExternal && (
            <span className="feedback__internal">
              <span className="feedback__internalIcon" />
              <Trans>Opinia z Fixly</Trans>
            </span>
          )}
          {props.allowReply && !props.reply && !showAddReply && (
            <LinkButton className="feedback__showAddReply" onClick={() => setShowAddReply(true)}>
              <Trans>Dodaj komentarz</Trans>
            </LinkButton>
          )}
        </TextMeta>
      </div>

      {renderAddReply()}
      {props.reply && props.reply.comment && renderReply(props.reply)}
    </div>
  );
};

export default memo(Feedback);
